(() => {
  const initializePressCarousel = (root) => {
    if (root.dataset.pressCarouselReady === 'true') return;

    const viewport = root.querySelector('[data-press-viewport]');
    const previous = root.querySelector('[data-press-prev]');
    const next = root.querySelector('[data-press-next]');

    if (!viewport || !previous || !next) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let updateFrame = null;

    const updateControls = () => {
      const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      previous.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= maximumScroll - 2;
    };

    const queueControlUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateControls();
        updateFrame = null;
      });
    };

    const move = (direction) => {
      viewport.scrollBy({
        left: direction * Math.max(viewport.clientWidth * 0.72, 220),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    viewport.addEventListener('scroll', queueControlUpdate, { passive: true });
    window.addEventListener('resize', queueControlUpdate, { passive: true });

    updateControls();
    root.dataset.pressCarouselReady = 'true';
  };

  const initializeProductGallery = (root) => {
    const gallery = root.querySelector('[data-wardrobe-gallery]');
    if (!gallery || gallery.dataset.galleryReady === 'true') return;

    const mainImage = gallery.querySelector('[data-gallery-main-image]');
    const caption = gallery.querySelector('[data-gallery-caption]');
    const counter = gallery.querySelector('[data-gallery-counter]');
    const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));

    if (!mainImage || !thumbnails.length) return;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const source = thumbnail.dataset.gallerySrc;
        const sourceSet = thumbnail.dataset.gallerySrcset;
        const alt = thumbnail.dataset.galleryAlt || '';

        if (source) mainImage.src = source;
        if (sourceSet) mainImage.srcset = sourceSet;
        mainImage.alt = alt;

        thumbnails.forEach((item) => {
          const isActive = item === thumbnail;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });

        if (caption) caption.textContent = alt;
        if (counter) counter.textContent = `${thumbnail.dataset.galleryIndex} / ${thumbnails.length}`;
      });
    });

    gallery.dataset.galleryReady = 'true';
  };

  const initializeWardrobe = (root) => {
    initializePressCarousel(root);
    initializeProductGallery(root);
    if (root.dataset.wardrobeReady === 'true') return;

    const form = root.querySelector('[data-wardrobe-form]');
    const selects = Array.from(root.querySelectorAll('[data-scent-select]'));
    const submit = root.querySelector('[data-wardrobe-submit]');
    const summary = root.querySelector('[data-selection-summary]');
    const errorNode = root.querySelector('[data-form-error]');
    const checkoutReady = root.dataset.checkoutReady === 'true';
    const checkoutMessage = root.dataset.checkoutMessage || '';
    const wardrobeVariants = new Map();
    const wardrobeVariantsNode = root.querySelector('[data-wardrobe-variants]');
    if (wardrobeVariantsNode) {
      try {
        JSON.parse(wardrobeVariantsNode.textContent).forEach((variant) => {
          wardrobeVariants.set(variant.componentIds.map(String).sort().join('-'), variant);
        });
      } catch (error) {
        console.error('Unable to parse scent wardrobe bundles', error);
      }
    }
    const selectedWardrobe = () => wardrobeVariants.get(selects.map((select) => String(select.value)).sort().join('-'));
    const isArabic = root.dataset.locale === 'ar';
    const copy = {
      selectionPrefix: root.dataset.copySelectionPrefix || 'Your wardrobe:',
      unavailable: root.dataset.copyUnavailable || 'The scent wardrobe is temporarily unavailable.',
      distinct: root.dataset.copyDistinct || 'Choose three different available scents.',
      adding: root.dataset.copyAdding || 'Adding your wardrobe…',
      addButton: root.dataset.copyAddButton || 'Add the $139 scent wardrobe',
      addFailed: root.dataset.copyAddFailed || 'The scent wardrobe could not be added. Check your selections and try again.',
    };
    const cartSlotLabels = [
      root.dataset.cartSlotOne || 'Every day',
      root.dataset.cartSlotTwo || 'Something lighter',
      root.dataset.cartSlotThree || 'The evening',
    ];
    const selectionSeparator = isArabic ? '، ' : ', ';

    if (!form || selects.length !== 3 || !submit) return;

    const setError = (message = '') => {
      if (errorNode) errorNode.textContent = message;
    };

    const selectedOptions = () => selects.map((select) => select.options[select.selectedIndex]).filter(Boolean);

    const setInitialSelections = () => {
      const usedIds = new Set();

      selects.forEach((select) => {
        const preferred = Array.from(select.options).find((option) => (
          option.dataset.scentKey === select.dataset.preferredScent
          && !option.disabled
          && !usedIds.has(option.value)
        ));
        const fallback = Array.from(select.options).find((option) => !option.disabled && !usedIds.has(option.value));
        const chosen = preferred || fallback;
        if (chosen) {
          select.value = chosen.value;
          usedIds.add(chosen.value);
        }
      });
    };

    const syncOptionAvailability = () => {
      const values = selects.map((select) => select.value);

      selects.forEach((select, selectIndex) => {
        Array.from(select.options).forEach((option) => {
          const selectedElsewhere = values.some((value, valueIndex) => valueIndex !== selectIndex && value === option.value);
          option.disabled = option.dataset.unavailable === 'true' || selectedElsewhere;
        });
      });
    };

    const syncStage = () => {
      selectedOptions().forEach((option, index) => {
        const slot = String(index + 1);
        const image = root.querySelector(`[data-slot-image="${slot}"]`);
        const name = root.querySelector(`[data-slot-name="${slot}"]`);
        const scentName = option.dataset.scentName || option.textContent.trim();

        if (name) name.textContent = scentName;
        if (image && option.dataset.imageUrl) {
          image.src = option.dataset.imageUrl;
          image.alt = isArabic ? `زيت اللحية العربي برائحة ${scentName}` : `${scentName} Arabian Beard Oil`;
          if (option.dataset.imageWidth) image.width = Number(option.dataset.imageWidth);
          if (option.dataset.imageHeight) image.height = Number(option.dataset.imageHeight);
        }
      });
    };

    const syncState = () => {
      syncOptionAvailability();
      syncStage();

      const options = selectedOptions();
      const ids = options.map((option) => option.value);
      const names = options.map((option) => option.dataset.scentName || option.textContent.trim());
      const hasThreeDistinctScents = ids.length === 3 && new Set(ids).size === 3;

      if (summary) {
        summary.textContent = hasThreeDistinctScents ? `${copy.selectionPrefix} ${names.join(selectionSeparator)}` : '';
      }

      const wardrobeVariant = selectedWardrobe();
      submit.disabled = !checkoutReady || !wardrobeVariant?.available || !hasThreeDistinctScents;
      if (!checkoutReady || !wardrobeVariant?.available) {
        setError(checkoutMessage || copy.unavailable);
      } else {
        setError(hasThreeDistinctScents ? '' : copy.distinct);
      }
    };

    const setLoading = (isLoading) => {
      submit.disabled = isLoading || !checkoutReady || !selectedWardrobe()?.available;
      submit.setAttribute('aria-busy', String(isLoading));
      submit.textContent = isLoading ? copy.adding : copy.addButton;
    };

    selects.forEach((select) => select.addEventListener('change', syncState));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const wardrobeVariant = selectedWardrobe();
      if (!checkoutReady || !wardrobeVariant?.available || !Number.isFinite(Number(wardrobeVariant.id))) return;

      const ids = selects.map((select) => select.value);
      if (ids.length !== 3 || new Set(ids).size !== 3) {
        setError(copy.distinct);
        return;
      }

      setError('');
      setLoading(true);

      try {
        const selectedNames = selectedOptions().map((option) => option.dataset.scentName || option.textContent.trim());
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{
              id: Number(wardrobeVariant.id),
              quantity: 1,
              properties: {
                [cartSlotLabels[0]]: selectedNames[0],
                [cartSlotLabels[1]]: selectedNames[1],
                [cartSlotLabels[2]]: selectedNames[2],
                '_Arabian Beard Oil variant IDs': ids.join(', '),
                '_Campaign': 'US Arabian Scent Wardrobe',
              },
            }],
          }),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(isArabic ? copy.addFailed : (result.description || result.message || copy.addFailed));
        }

        window.location.assign(root.dataset.cartUrl || '/cart');
      } catch (error) {
        setError(error.message || copy.addFailed);
        setLoading(false);
      }
    });

    setInitialSelections();
    syncState();
    root.dataset.wardrobeReady = 'true';
  };

  const initialize = () => {
    document.querySelectorAll('.us-scent-wardrobe').forEach(initializeWardrobe);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
